import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { assetKeys } from '@/shared/lib/query/queryKeys';
import type { OwnerEditHook } from '@/shared/types/editHooks';

import { useTeamOwners, useUserOwners } from '@/entities/owner/model';

import type { AssetBaseInfo } from '@/features/asset-detail/api';
import { useUpdateAssetOwners } from '@/features/asset-detail/model';

type UseAssetOwnerEditProps = {
  assetId: string;
  entityType: string;
  fqn: string;
};

export const useAssetOwnerEdit = ({ assetId, entityType, fqn }: UseAssetOwnerEditProps): OwnerEditHook => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('teams');
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const assetData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));

  const currentOwners = useMemo(() => assetData?.owners || [], [assetData?.owners]);

  const { data: teamOwners = [], isLoading: isLoadingTeams } = useTeamOwners();
  const { data: userOwners = [], isLoading: isLoadingUsers } = useUserOwners();
  const { mutate: updateOwners, isPending: isUpdating } = useUpdateAssetOwners(entityType, assetId, fqn);

  const currentTeamId = useMemo(
    () => currentOwners.find((owner) => owner.type === 'team')?.id || null,
    [currentOwners]
  );

  const currentUserIds = useMemo(() => {
    const ids = currentOwners.filter((o) => o.type === 'user').map((o) => o.id);

    return Array.from(new Set(ids)).sort();
  }, [currentOwners]);

  useEffect(() => {
    setSelectedTeamId(currentTeamId);
    setSelectedUserIds(currentUserIds);
  }, [currentTeamId, currentUserIds]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestAssetData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));
    const latestOwners = latestAssetData?.owners || [];

    if (latestOwners.length > 0) {
      const teamOwner = latestOwners.find((owner) => owner.type === 'team');
      const userOwnerIds = latestOwners.filter((owner) => owner.type === 'user').map((owner) => owner.id);

      setSelectedTeamId(teamOwner?.id || null);
      setSelectedUserIds(userOwnerIds);
    }
  }, [queryClient, entityType, fqn]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    const latestAssetData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));
    const latestOwners = latestAssetData?.owners || [];

    if (latestOwners.length > 0) {
      const teamOwner = latestOwners.find((owner) => owner.type === 'team');
      const userOwnerIds = latestOwners.filter((owner) => owner.type === 'user').map((owner) => owner.id);

      setSelectedTeamId(teamOwner?.id || null);
      setSelectedUserIds(userOwnerIds);
    } else {
      setSelectedTeamId(null);
      setSelectedUserIds([]);
    }
  }, [queryClient, entityType, fqn]);

  const handleTeamSelect = useCallback((teamId: string) => {
    setSelectedTeamId((prev) => (prev === teamId ? null : teamId));
    setSelectedUserIds([]);
  }, []);

  const handleUserSelect = useCallback((userId: string, checked: boolean) => {
    setSelectedUserIds((prev) => (checked ? [...prev, userId] : prev.filter((id) => id !== userId)));
    setSelectedTeamId(null);
  }, []);

  const handleUpdate = useCallback(() => {
    const latestAssetData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));
    const latestOwners = latestAssetData?.owners || [];

    updateOwners(
      {
        selectedTeamId,
        selectedUserIds,
        teams: teamOwners,
        users: userOwners,
        currentOwners: latestOwners,
      },
      {
        onSuccess: () => {
          setIsOpen(false);
        },
      }
    );
  }, [selectedTeamId, selectedUserIds, teamOwners, userOwners, queryClient, entityType, fqn, updateOwners]);

  const hasChanges = useMemo(() => {
    const teamChanged = currentTeamId !== selectedTeamId;
    const usersChanged =
      currentUserIds.length !== selectedUserIds.length || !currentUserIds.every((id) => selectedUserIds.includes(id));

    return teamChanged || usersChanged;
  }, [currentTeamId, currentUserIds, selectedTeamId, selectedUserIds]);

  return {
    isOpen,
    activeTab,
    selectedTeamId,
    selectedUserIds,
    teamOwners,
    userOwners,
    currentOwners,
    isLoadingTeams,
    isLoadingUsers,
    isUpdating,
    hasChanges,
    handleOpen,
    handleClose,
    setActiveTab,
    handleTeamSelect,
    handleUserSelect,
    handleUpdate,
  };
};
